import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'
import { fromFilesArrayToSavedFiles, getInvalidDocuments } from '../helpers'

export interface SavedFile {
  emission: string
  id: number
  originalName: string
  type: string
  url?: string
}

export interface SavedFiles {
  identity?: SavedFile
  proofOfResidence?: SavedFile
  anvisaAuthorization?: SavedFile
  bondProof?: SavedFile
}

interface FetchDocumentsResponse {
  documents: SavedFile[]
  success: boolean
}

export default function useDocuments() {
  const [loading, setLoading] = useState(false)
  const [identity, setIdentity] = useState<File>()
  const [identityIssueDate, setIdentityIssueDate] = useState('')
  const [proofOfResidence, setProofOfResidence] = useState<File>()
  const [proofOfResidenceIssueDate, setProofOfResidenceIssueDate] = useState('')
  const [isProofOfResidenceInOtherName, setIsProofOfResidenceInOtherName] = useState(false)
  const [anvisaAuthorization, setAnvisaAuthorization] = useState<File>()
  const [anvisaAuthorizationIssueDate, setAnvisaAuthorizationIssueDate] = useState('')
  const [bondProof, setBondProof] = useState<File>()
  const [bondProofIssueDate, setBondProofIssueDate] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [savedFiles, setSavedFiles] = useState<SavedFiles>({})

  const fetchDocuments = async () => {
    setLoading(true)
    try {
      const { data } = await api.get<FetchDocumentsResponse>('/my-account/documents')
      if (!data.success) {
        toast.error('Erro ao carregar documentos. Tente novamente.')
        return
      }
      const newSavedFiles = fromFilesArrayToSavedFiles(data.documents)
      if (newSavedFiles.bondProof) {
        setIsProofOfResidenceInOtherName(true)
      }
      setIdentityIssueDate(newSavedFiles.identity?.emission ?? '')
      setProofOfResidenceIssueDate(newSavedFiles.proofOfResidence?.emission ?? '')
      setAnvisaAuthorizationIssueDate(newSavedFiles.anvisaAuthorization?.emission ?? '')
      setBondProofIssueDate(newSavedFiles.bondProof?.emission ?? '')
      setSavedFiles(newSavedFiles)
    } catch (err) {
      toast.error('Erro ao carregar documentos. Tente novamente.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveDocuments = async () => {
    const validationErrors = getInvalidDocuments({
      identity,
      identityIssueDate,
      proofOfResidence,
      proofOfResidenceIssueDate,
      anvisaAuthorization,
      anvisaAuthorizationIssueDate,
      bondProof,
      bondProofIssueDate,
      isProofOfResidenceInOtherName,
      savedFiles
    })

    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      toast.warning('Preencha os campos obrigatórios antes de salvar')
      return
    }

    const filesToUpload = getFilesToUpload()
    if (filesToUpload.length === 0) {
      toast.warning('Selecione pelo menos um documento para enviar')
      return
    }

    setLoading(true)
    try {
      await Promise.all(getUploadsPromises())
      toast.success('Documentos salvos com sucesso!')

      setIdentity(undefined)
      setProofOfResidence(undefined)
      setAnvisaAuthorization(undefined)
      setBondProof(undefined)

      await fetchDocuments()
    } catch (err) {
      toast.error('Erro ao salvar documentos')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const getUploadsPromises = () => {
    const filesToUpload = getFilesToUpload()

    return filesToUpload.map((fileObj) => {
      const formData = new FormData()

      formData.append('file', fileObj.file)
      formData.append('type', fileObj.type)
      formData.append('emission', fileObj.emission)

      return api.post('/my-account/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    })
  }

  const getFilesToUpload = () => {
    const filesToUpload: {
      file: File
      type: string
      emission: string
    }[] = []

    if (identity) {
      filesToUpload.push({
        file: identity,
        type: 'IDENTIDADE',
        emission: getFormattedDate(identityIssueDate)
      })
    }

    if (proofOfResidence) {
      filesToUpload.push({
        file: proofOfResidence,
        type: 'RESIDENCIAL',
        emission: getFormattedDate(proofOfResidenceIssueDate)
      })
    }

    if (anvisaAuthorization) {
      filesToUpload.push({
        file: anvisaAuthorization,
        type: 'ANVISA',
        emission: getFormattedDate(anvisaAuthorizationIssueDate)
      })
    }

    if (bondProof) {
      filesToUpload.push({
        file: bondProof,
        type: 'VINCULO',
        emission: getFormattedDate(bondProofIssueDate)
      })
    }

    return filesToUpload
  }

  const getFormattedDate = (unformattedDate: string): string => {
    const parts = unformattedDate.split('/')
    if (parts.length === 2) {
      const [month, year] = parts
      return `${year}-${month}-01`
    }
    return parts.reverse().join('-')
  }

  const removeFieldFromErrors = useCallback((fieldName: string) => {
    setErrors((prevErrors) => prevErrors?.filter((error) => error !== fieldName))
  }, [])

  const changeFieldValue = useCallback(
    (fieldName: string, newValue?: string | boolean | File) => {
      switch (fieldName) {
        case 'identity':
          setIdentity(newValue as File | undefined)
          break
        case 'identityIssueDate':
          setIdentityIssueDate(newValue as string)
          break
        case 'proofOfResidence':
          setProofOfResidence(newValue as File | undefined)
          break
        case 'proofOfResidenceIssueDate':
          setProofOfResidenceIssueDate(newValue as string)
          break
        case 'anvisaAuthorization':
          setAnvisaAuthorization(newValue as File | undefined)
          break
        case 'anvisaAuthorizationIssueDate':
          setAnvisaAuthorizationIssueDate(newValue as string)
          break
        case 'bondProof':
          setBondProof(newValue as File | undefined)
          break
        case 'bondProofIssueDate':
          setBondProofIssueDate(newValue as string)
          break
        case 'isProofOfResidenceInOtherName':
          setIsProofOfResidenceInOtherName(newValue as boolean)
          break
        default:
          return
      }

      removeFieldFromErrors(fieldName)
    },
    [removeFieldFromErrors]
  )

  const changeIsProofOfResidenceInOtherName = useCallback((newValue: boolean) => {
    setIsProofOfResidenceInOtherName(newValue)
    setErrors((prevErrors) =>
      prevErrors?.filter(
        (error) =>
          error !== 'proofOfResidence' &&
          error !== 'proofOfResidenceIssueDate' &&
          error !== 'bondProof' &&
          error !== 'bondProofIssueDate'
      )
    )
  }, [])

  useEffect(() => {
    fetchDocuments()
  }, [])

  return {
    loading,
    identity,
    identityIssueDate,
    proofOfResidence,
    proofOfResidenceIssueDate,
    isProofOfResidenceInOtherName,
    anvisaAuthorization,
    anvisaAuthorizationIssueDate,
    bondProof,
    bondProofIssueDate,
    errors,
    savedFiles,
    handleSaveDocuments,
    changeIsProofOfResidenceInOtherName,
    changeFieldValue
  }
}
